import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import { createPortal } from "react-dom";

import { ReactComponent as InputSearch } from "@assets/images/icons/input-search.svg";

import "./BaseDropdown.sass";

const MENU_PORTAL_Z_INDEX = 10100;
const MENU_GAP_PX = 8;

function getTriggerElement(root) {
  if (!root) return null;
  return (
    root.querySelector(
      "button[aria-expanded], button[type='button'], .base-dropdown__button",
    ) || root.firstElementChild
  );
}

export const BaseDropdown = ({
  trigger,
  children,
  className = "",
  hasSearch = false,
  align = "right",
  useMenuPortal = false,
  menuClassName = "",
  matchTriggerWidth = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [menuPosition, setMenuPosition] = useState(null);
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);

  const updateMenuPosition = useCallback(() => {
    if (!useMenuPortal || !dropdownRef.current) return;

    const triggerEl = getTriggerElement(dropdownRef.current);
    if (!triggerEl) return;

    const rect = triggerEl.getBoundingClientRect();
    const viewportPadding = 8;
    const isNarrowViewport = window.innerWidth < 1000;

    let left = rect.left;
    let transform = "";

    if (align === "right") {
      left = rect.right;
      transform = "translateX(-100%)";
    } else if (align === "center") {
      left = rect.left + rect.width / 2;
      transform = "translateX(-50%)";
    }

    setMenuPosition({
      position: "fixed",
      top: rect.bottom + MENU_GAP_PX,
      left,
      transform,
      zIndex: MENU_PORTAL_Z_INDEX,
      ...(matchTriggerWidth
        ? {
            width: rect.width,
            minWidth: rect.width,
            maxWidth: rect.width,
            boxSizing: "border-box",
          }
        : isNarrowViewport
          ? { maxWidth: window.innerWidth - viewportPadding * 2 }
          : {}),
    });
  }, [align, matchTriggerWidth, useMenuPortal]);

  useLayoutEffect(() => {
    if (!isOpen || !useMenuPortal) {
      setMenuPosition(null);
      return undefined;
    }

    updateMenuPosition();
    const rafId = requestAnimationFrame(() => updateMenuPosition());

    const onReposition = () => updateMenuPosition();
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [isOpen, updateMenuPosition, useMenuPortal]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = dropdownRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inTrigger && !inMenu) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setSearchTerm("");
  }, [isOpen]);

  const toggleDropdown = () => setIsOpen((prev) => !prev);
  const closeDropdown = () => setIsOpen(false);

  const menuContent = (
    <>
      {hasSearch && (
        <div className="base-dropdown__search-wrapper">
          <span className="base-dropdown__search-icon">
            <InputSearch />
          </span>
          <input
            type="text"
            className="base-dropdown__search-input"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size={1}
            autoFocus
          />
        </div>
      )}

      {typeof children === "function"
        ? children({ closeDropdown, searchTerm })
        : children}
    </>
  );

  const menuElement = isOpen ? (
    <div
      ref={menuRef}
      className={`base-dropdown__menu base-dropdown__menu--align-${align} ${
        useMenuPortal ? "base-dropdown__menu--portal" : ""
      } ${menuClassName}`.trim()}
      style={useMenuPortal ? menuPosition : undefined}
    >
      {menuContent}
    </div>
  ) : null;

  return (
    <div className={`base-dropdown ${className}`} ref={dropdownRef}>
      {React.cloneElement(trigger, {
        "aria-expanded": isOpen,
        onClick: (e) => {
          if (trigger.props.onClick) trigger.props.onClick(e);
          toggleDropdown();
        },
      })}

      {useMenuPortal
        ? menuPosition && menuElement
          ? createPortal(menuElement, document.body)
          : null
        : menuElement}
    </div>
  );
};
